// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Cloudflare Turnstile in the app's two forms that send mail
 * (security fix plan 2026-10, S-04): the sign-in dialog's e-mail form and
 * the plans dialog's invoice request (`src/ui/xrPlansUI.ts`). The welcome
 * page has its own copy of this in plain JavaScript (`public/welcome.js`).
 *
 * On only when the build carries a site key (`VITE_TURNSTILE_SITE_KEY`;
 * the server checks the token with `TURNSTILE_SECRET`, set together):
 * without one nothing is loaded and no token is sent. With one, the
 * widget script comes from Cloudflare, our processor for the whole site
 * (`challenges.cloudflare.com`, allowed in `scripts/check-own-hosts.ts`),
 * only once a form that needs it is shown; the widget is managed and
 * shows itself only when it must ("interaction-only"), and a token is
 * fetched when the form is sent. A token is used once.
 */

interface TurnstileRenderOptions {
  sitekey: string
  action?: string
  appearance?: 'always' | 'execute' | 'interaction-only'
  execution?: 'render' | 'execute'
  callback?: (token: string) => void
  'error-callback'?: () => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
}

export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string | undefined
  execute(widgetId: string): void
  reset(widgetId: string): void
  remove(widgetId: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    ayniTurnstileReady?: () => void
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=ayniTurnstileReady&render=explicit'
/** How long a token is waited for before the form is sent without one (the server then says `challenge_failed`). */
const TOKEN_WAIT_MS = 30_000

export const TURNSTILE_SITE_KEY: string = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '').trim()

/** Whether this build was given a site key: without one the forms carry no check. */
export function turnstileConfigured(siteKey: string = TURNSTILE_SITE_KEY): boolean {
  return siteKey.trim().length > 0
}

let loading: Promise<TurnstileApi | null> | null = null

/** The widget API, loading Cloudflare's script once; null when it cannot load (blocked, offline). */
export function loadTurnstile(): Promise<TurnstileApi | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (loading) return loading
  loading = new Promise<TurnstileApi | null>((resolve) => {
    window.ayniTurnstileReady = () => resolve(window.turnstile ?? null)
    const script = document.createElement('script')
    script.src = SCRIPT
    script.async = true
    script.defer = true
    script.onerror = () => {
      loading = null
      resolve(null)
    }
    document.head.appendChild(script)
  })
  return loading
}

export interface Challenge {
  /** Where the widget shows itself when it must; put it in the form. */
  element: HTMLElement
  /** A fresh token for one request, or null when none could be had. */
  token(): Promise<string | null>
  remove(): void
}

/**
 * A challenge for one form: its element goes in the form, and `token()`
 * is awaited before the request is sent. Null when no site key is
 * configured, so the caller sends the form as before.
 */
export function attachChallenge(opts: { siteKey?: string; action?: string } = {}): Challenge | null {
  const siteKey = (opts.siteKey ?? TURNSTILE_SITE_KEY).trim()
  if (!turnstileConfigured(siteKey)) return null
  const element = document.createElement('div')
  element.className = 'xr-challenge'
  let widgetId: string | undefined
  let waiting: ((token: string | null) => void) | null = null
  let api: TurnstileApi | null = null
  const settle = (token: string | null) => {
    const w = waiting
    waiting = null
    w?.(token)
  }
  const ready = async (): Promise<boolean> => {
    api = api ?? (await loadTurnstile())
    if (!api || !element.isConnected) return false
    if (widgetId === undefined) {
      widgetId = api.render(element, {
        sitekey: siteKey,
        action: opts.action,
        appearance: 'interaction-only',
        execution: 'execute',
        callback: (token) => settle(token),
        'error-callback': () => settle(null),
        'expired-callback': () => settle(null),
        'timeout-callback': () => settle(null),
      })
    }
    return widgetId !== undefined
  }
  return {
    element,
    async token() {
      if (!(await ready()) || !api || widgetId === undefined) return null
      const id = widgetId
      return new Promise<string | null>((resolve) => {
        const timer = setTimeout(() => settle(null), TOKEN_WAIT_MS)
        waiting = (token) => {
          clearTimeout(timer)
          resolve(token)
        }
        try {
          api!.reset(id)
          api!.execute(id)
        } catch {
          settle(null)
        }
      })
    },
    remove() {
      if (api && widgetId !== undefined) {
        try {
          api.remove(widgetId)
        } catch {
          // Already gone with its dialog.
        }
      }
      widgetId = undefined
      element.remove()
    },
  }
}
